// Záložka Leady v Klientech (hlášení #139, Kristina; mockup schválený 7. 10. 2026).
// Dřív tabulka s klientskými sloupci (Faktura, Balíček, Stav tréninků…), které jsou u leadu
// vždy prázdné, a stage se ukazovala jako „?“. Teď: dlaždice podle Notion stage (společná řeč
// se sales), sloupce, co u leadu dávají smysl, a klepnutí otevře kartu kontaktu ze sales
// (owner, manažer, CSM) přímo tady v Klientech (?lead=<id>), kde je blok Výsledek (Deal, Prohra).
import React, { useMemo, useState } from "react";
import { Search, X, ChevronRight, AlertTriangle, Users, Eye, EyeOff, Archive } from "lucide-react";
import { DlazdiceFiltr, Stitek, EmptyState, SkeletonRows, Hlaska } from "./ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { getCurrentUser } from "../lib/auth.js";

// Pořadí a tóny stage jako v sales (Notion: Lead 👏, Kvalifikace ☎️, Trial 🏋️, Deal 🤝, Prohra ❌, NQlead 💀).
export const STAGE_VOLBY = [
  { key: "Lead", ton: "neutralni" },
  { key: "Kvalifikace", ton: "info" },
  { key: "Trial", ton: "ok" },
  { key: "Trial proběhl", ton: "ok" },
  { key: "Deal", ton: "znacka" },
  { key: "Prohra", ton: "problem" },
  { key: "NQlead", ton: "neutralni" },
];
const TON_STAGE = Object.fromEntries(STAGE_VOLBY.map((s) => [s.key, s.ton]));

// Notion stage může mít emoji nebo jiný zápis („NQlead 💀“, „NQLead“, „Uzavřeno ✅“).
export function normStage(stage) {
  const s = String(stage || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z ]+/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return "";
  if (s.startsWith("nq")) return "NQlead";
  if (s.startsWith("lead")) return "Lead";
  if (s.startsWith("kvalifikace")) return "Kvalifikace";
  if (s.startsWith("trial probehl")) return "Trial proběhl";
  if (s.startsWith("trial")) return "Trial";
  if (s.startsWith("deal") || s.startsWith("uzav")) return "Deal";
  if (s.startsWith("prohra")) return "Prohra";
  return "";
}

const KLIC_SKRYTE = "fw40-leady-skryte";
// 3. 11. 2026 (Láďa): archivované leady (archivovan z /api/clients, podle sales_info.archiv)
// nejsou v žádné dlaždici, počtu ani tabulce. Owner je vidí jen v dlaždici Archiv.
const ARCHIV = "Archiv";
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[.,;:'"!?]/g, "").replace(/\s+/g, " ").trim();
const dm = (iso) => { if (!iso) return ""; const d = new Date(iso); if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10); return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`; };
const dmKratce = (iso) => { if (!iso) return ""; const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number); return y ? `${d}. ${m}.` : ""; };
const telefonHezky = (t) => { const c = String(t || "").replace(/\s+/g, ""); const m = c.match(/^(\+420)?(\d{3})(\d{3})(\d{3})$/); return m ? `${m[2]} ${m[3]} ${m[4]}` : String(t || ""); };
const krestniPrijmeni = (j) => { const p = String(j || "").trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : p[0] || ""; };

export function StagePilulka({ stage, velikost = "mala" }) {
  const s = normStage(stage) || String(stage || "").trim();
  if (!s) return <span className="text-slate-400 text-sm">—</span>;
  return <Stitek ton={TON_STAGE[s] || "neutralni"} velikost={velikost}>{s}</Stitek>;
}

export default function LeadyPrehled({ leady, loading, error, onDetail, onKarta }) {
  const uzka = useUzkaObrazovka();
  const role = String(getCurrentUser()?.role || "").toLowerCase();
  const smiKartu = ["owner", "manager", "csm"].includes(role);
  const jeOwner = role === "owner";
  const [stage, setStage] = useState([]);
  const [archiv, setArchiv] = useState(false);
  const aktivni = useMemo(() => leady.filter((l) => !l.archivovan), [leady]);
  const archivovane = useMemo(() => leady.filter((l) => l.archivovan), [leady]);
  const [hledat, setHledat] = useState("");
  // Skryté dlaždice (7. 10. 2026, Láďa): jako sloupce kanbanu, ikona oka v rohu dlaždice, skryté
  // dole jako pilulky. Pamatuje se per uživatel v prohlížeči, skrytá stage se v tabulce neukazuje.
  const [skryte, setSkryte] = useState(() => { try { return JSON.parse(localStorage.getItem(KLIC_SKRYTE) || "[]"); } catch { return []; } });
  const ulozSkryte = (v) => { setSkryte(v); try { localStorage.setItem(KLIC_SKRYTE, JSON.stringify(v)); } catch { /* bez paměti */ } };
  const skryj = (k) => { ulozSkryte([...skryte.filter((x) => x !== k), k]); setStage((v) => v.filter((x) => x !== k)); if (k === ARCHIV) setArchiv(false); };
  const odkryj = (k) => ulozSkryte(skryte.filter((x) => x !== k));

  const pocty = useMemo(() => {
    const p = {};
    for (const l of aktivni) { const s = normStage(l.stage) || "(bez stage)"; p[s] = (p[s] || 0) + 1; }
    p[ARCHIV] = archivovane.length;
    return p;
  }, [aktivni, archivovane]);

  const filtrovane = useMemo(() => {
    const q = norm(hledat);
    return (archiv ? archivovane : aktivni).filter((l) => {
      const st = normStage(l.stage) || "(bez stage)";
      if (!archiv && skryte.includes(st)) return false;
      if (!archiv && stage.length && !stage.includes(st)) return false;
      if (q && !norm(l.klient).includes(q) && !String(l.telefon || "").replace(/\s+/g, "").includes(q.replace(/\s+/g, ""))) return false;
      return true;
    }).sort((a, b) => String(b.salesZmeneno || b.datumVytvoreni || "").localeCompare(String(a.salesZmeneno || a.datumVytvoreni || "")));
  }, [aktivni, archivovane, archiv, stage, hledat, skryte]);

  const prepni = (k) => {
    if (k === ARCHIV) { setArchiv((v) => !v); setStage([]); return; }
    setArchiv(false);
    setStage((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]));
  };
  const zapnuta = (k) => (k === ARCHIV ? archiv : stage.includes(k));
  const otevri = (l) => {
    if (smiKartu && l.leadId && onKarta) onKarta(l);
    else onDetail?.(l);
  };
  const zkusebniText = (l) => {
    const z = l.salesZkusebni;
    if (!z || !z.datum) return "";
    return `${dmKratce(z.datum)}${z.cas ? ` ${z.cas}` : ""}${z.trener ? `, ${z.trener}` : ""}${z.stav === "zruseno" ? " (zrušený)" : ""}`;
  };
  const duvodText = (l) => (["Prohra", "NQlead"].includes(normStage(l.stage)) ? l.salesDuvod || "" : "");
  const kdoText = (l) => l.salesKdo || l.salesOsoba || "";
  const dlazdice = [
    ...STAGE_VOLBY.map((s) => ({ ...s, pocet: pocty[s.key] || 0 })),
    ...(pocty["(bez stage)"] ? [{ key: "(bez stage)", ton: "neutralni", pocet: pocty["(bez stage)"] }] : []),
    ...(jeOwner ? [{ key: ARCHIV, ton: "neutralni", pocet: archivovane.length, ikona: Archive }] : []),
  ];
  const viditelne = dlazdice.filter((d) => !skryte.includes(d.key));
  const archivText = (l) => (l.leadArchiv ? ["archivováno", dm(l.leadArchiv.kdy), l.leadArchiv.kdo ? String(l.leadArchiv.kdo).split(" ")[0] : ""].filter(Boolean).join(", ") : "archivováno");

  return (
    <div className="space-y-3">
      {/* 7 dlaždic se do jedné řady vejde až na širokém monitoru (2xl), jinak se popisek lámal přes číslo. */}
      <div className={`grid gap-2 ${uzka ? "grid-cols-2" : `grid-cols-3 lg:grid-cols-4 ${viditelne.length >= 8 ? "2xl:grid-cols-8" : "2xl:grid-cols-7"}`}`}>
        {viditelne.map((d) => (
          <div key={d.key} className="relative [&>div>button]:!pr-8">
            <DlazdiceFiltr popisek={d.key} hodnota={d.pocet} ton={d.ton} ikona={d.ikona} zapnuto={zapnuta(d.key)} onClick={() => prepni(d.key)} />
            <button type="button" onClick={() => skryj(d.key)} title={`Skrýt ${d.key}`} aria-label={`Skrýt dlaždici ${d.key}`}
              className={`absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${zapnuta(d.key) ? "text-white/50 hover:bg-white/15 hover:text-white" : "text-slate-300 hover:bg-slate-100 hover:text-slate-600"}`}>
              <EyeOff className="h-[13px] w-[13px]" />
            </button>
          </div>
        ))}
      </div>
      {skryte.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-slate-500">
          <span className="font-semibold">Skryté:</span>
          {skryte.map((k) => (
            <button key={k} type="button" onClick={() => odkryj(k)} title="Zobrazit zpět"
              className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-dashed border-slate-300 bg-white px-2.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50">
              {k} <span className="text-slate-400">·</span> <span className="tabular-nums">{pocty[k] || 0}</span><Eye className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="text" value={hledat} onChange={(e) => setHledat(e.target.value)} placeholder="Hledat jméno nebo telefon" className="field w-full !pl-9" />
          {hledat && <button type="button" onClick={() => setHledat("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:text-slate-700" aria-label="Smazat hledání"><X className="h-4 w-4" /></button>}
        </div>
        {(stage.length > 0 || archiv || hledat) && <button type="button" className="btn btn-secondary shrink-0" onClick={() => { setStage([]); setArchiv(false); setHledat(""); }}><X className="h-4 w-4" /> Zrušit vše</button>}
      </div>
      <div className="px-1 text-[11px] font-semibold text-slate-400">{archiv ? `Archiv: ${filtrovane.length} z ${archivovane.length} archivovaných leadů` : `Zobrazeno ${filtrovane.length} z ${aktivni.length}${skryte.filter((k) => k !== ARCHIV).length ? ` (bez skrytých: ${skryte.filter((k) => k !== ARCHIV).join(", ")})` : ""}`}{smiKartu ? ", klepnutím otevřeš kartu kontaktu" : ""}</div>

      {loading && <SkeletonRows rows={8} />}
      {error && <Hlaska ton="problem" title="Nepovedlo se načíst leady">{error}</Hlaska>}
      {!loading && !error && filtrovane.length === 0 && (archiv
        ? <EmptyState icon={Archive} title="V archivu není žádný lead" hint={hledat ? "Zruš hledání." : "Archivované leady se ukážou tady."} />
        : <EmptyState icon={Users} title="Žádný lead neodpovídá filtru" hint="Zkus jinou stage nebo zruš hledání." />)}

      {filtrovane.length > 0 && uzka && (
        <div className="space-y-2">
          {filtrovane.map((l) => (
            <button key={l.leadId || l.klient} type="button" onClick={() => otevri(l)} className={`polozka flex w-full items-center gap-3 p-3 text-left ${l.blacklist === "Ano" ? "border-l-[3px] border-l-rose-400" : ""}`}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-bold text-brand-700">{(l.klient || "?").trim().charAt(0).toUpperCase()}</span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5"><span className="truncate text-sm font-bold text-slate-900 demo-sensitive">{l.klient}</span>{l.blacklist === "Ano" && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-600" />}</span>
                <span className="mt-1 flex flex-wrap items-center gap-1">{l.archivovan ? <Stitek ton="neutralni" velikost="mala" ikona={Archive}>{archivText(l)}</Stitek> : <StagePilulka stage={l.stage} />}{duvodText(l) && <Stitek ton="neutralni" velikost="mala">{duvodText(l)}</Stitek>}</span>
                <span className="mt-1 block text-xs text-slate-500">{[telefonHezky(l.telefon), zkusebniText(l) && `zkušební ${zkusebniText(l)}`, l.salesZmeneno && `změna ${dm(l.salesZmeneno)}`].filter(Boolean).join(" · ")}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
            </button>
          ))}
        </div>
      )}

      {filtrovane.length > 0 && !uzka && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="py-3 px-4 text-left font-semibold">Lead</th>
                <th className="py-3 px-2 text-left font-semibold">Stage</th>
                <th className="py-3 px-2 text-left font-semibold">Telefon</th>
                <th className="py-3 px-2 text-left font-semibold hidden lg:table-cell">Sales / kouč</th>
                <th className="py-3 px-2 text-left font-semibold hidden lg:table-cell">Lokalita</th>
                <th className="py-3 px-2 text-left font-semibold">Zkušební</th>
                <th className="py-3 px-2 text-left font-semibold hidden xl:table-cell">Důvod</th>
                <th className="py-3 px-2 text-left font-semibold">Poslední změna</th>
                <th className="py-3 px-2 text-left font-semibold hidden xl:table-cell">Kdo</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtrovane.map((l) => (
                <tr key={l.leadId || l.klient} onClick={() => otevri(l)} className={`group cursor-pointer border-b border-slate-100 hover:bg-brand-50/50 ${l.blacklist === "Ano" ? "border-l-[3px] border-l-rose-400" : ""}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">{(l.klient || "?").trim().charAt(0).toUpperCase()}</span>
                      <span className="whitespace-nowrap font-semibold text-slate-900 demo-sensitive">{l.klient}</span>
                      {l.blacklist === "Ano" && <Stitek ton="problem" velikost="mala" ikona={AlertTriangle}>Blacklist</Stitek>}
                    </div>
                  </td>
                  <td className="py-3 px-2">{l.archivovan ? <Stitek ton="neutralni" velikost="mala" ikona={Archive}>{archivText(l)}</Stitek> : <StagePilulka stage={l.stage} />}</td>
                  <td className="py-3 px-2 whitespace-nowrap text-slate-700">{telefonHezky(l.telefon) || <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 hidden lg:table-cell text-slate-700">{kdoText(l) || l.trener || <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 hidden lg:table-cell">{l.lokalita ? <Stitek ton="neutralni" velikost="mala">{l.lokalita}</Stitek> : <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 whitespace-nowrap text-slate-700">{zkusebniText(l) || <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 hidden xl:table-cell text-slate-700">{duvodText(l) || <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 whitespace-nowrap text-slate-700">{dm(l.salesZmeneno || l.datumVytvoreni) || <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 hidden xl:table-cell text-slate-600">{krestniPrijmeni(kdoText(l)) || <span className="text-slate-400">—</span>}</td>
                  <td className="py-3 px-2 text-slate-300 group-hover:text-slate-500"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
